import { Button } from "../ui/button";
import { ArrowLeft, Briefcase, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";

export function CTASection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-20 overflow-hidden" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* For Investors */}
          <div
            className={`relative overflow-hidden rounded-2xl sm:rounded-3xl bg-hero-gradient p-6 sm:p-8 md:p-12 text-primary-foreground hover-lift transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <div className="absolute top-0 left-0 w-48 h-48 sm:w-64 sm:h-64 bg-primary-foreground/10 rounded-full -translate-x-1/2 -translate-y-1/2 animate-pulse-soft" />
            <div className="relative z-10">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-primary-foreground/20 flex items-center justify-center mb-4 sm:mb-6 animate-float">
                <Briefcase className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
                {t('public.cta.investor.title')}
              </h3>
              <p className="text-primary-foreground/80 text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed">
                {t('public.cta.investor.description').split('<br/>')[0]}<br className="sm:hidden" />
                {t('public.cta.investor.description').split('<br/>')[1]}
              </p>
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="gap-2 bg-primary-foreground text-primary hover:bg-primary-foreground/90 group w-full sm:w-auto"
              >
                <Link to="/investor-register">
                  {t('public.cta.investor.button')}
                  <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {/* For Project Holders */}
          <div
            className={`relative overflow-hidden rounded-2xl sm:rounded-3xl bg-card border-2 border-border p-6 sm:p-8 md:p-12 hover-lift transition-all duration-700 delay-150 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <div className="absolute top-0 left-0 w-48 h-48 sm:w-64 sm:h-64 bg-muted rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="relative z-10">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-primary/10 flex items-center justify-center mb-4 sm:mb-6 animate-float" style={{ animationDelay: '0.5s' }}>
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
                {t('public.cta.projectHolder.title')}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed">
                {t('public.cta.projectHolder.description')}
              </p>
              <Button asChild size="lg" className="gap-2 bg-hero-gradient hover:opacity-90 text-primary-foreground group w-full sm:w-auto">
                <Link to="/project-holder-register">
                  {t('public.cta.projectHolder.button')}
                  <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}